import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recordScore, getProgress, setLevel, unlockNext, getHighScore } from '../gamesLib';
import { sfx } from '../gameSound';

interface Props {
  onExit: () => void;
  challengeScore?: number | null;
}

const COLS = 10;
const ROWS = 20;
const CELL = 22;
// Drop interval (ms) at the start of each level — falls as you clear lines.
const START_SPEED = [800, 680, 560, 460, 370, 300, 240, 190];
// Lines to clear to beat a level.
const LINE_GOAL = 10;

// Tetromino shapes (each a list of rotation states; each state a list of [x,y] cells).
type Shape = { color: string; rot: number[][][] };
const SHAPES: Shape[] = [
  { color: '#4dd0e1', rot: [ // I
    [[0,1],[1,1],[2,1],[3,1]], [[2,0],[2,1],[2,2],[2,3]],
    [[0,2],[1,2],[2,2],[3,2]], [[1,0],[1,1],[1,2],[1,3]] ] },
  { color: '#fff176', rot: [ // O
    [[1,0],[2,0],[1,1],[2,1]], [[1,0],[2,0],[1,1],[2,1]],
    [[1,0],[2,0],[1,1],[2,1]], [[1,0],[2,0],[1,1],[2,1]] ] },
  { color: '#ba68c8', rot: [ // T
    [[1,0],[0,1],[1,1],[2,1]], [[1,0],[1,1],[2,1],[1,2]],
    [[0,1],[1,1],[2,1],[1,2]], [[1,0],[0,1],[1,1],[1,2]] ] },
  { color: '#81c784', rot: [ // S
    [[1,0],[2,0],[0,1],[1,1]], [[1,0],[1,1],[2,1],[2,2]],
    [[1,1],[2,1],[0,2],[1,2]], [[0,0],[0,1],[1,1],[1,2]] ] },
  { color: '#e57373', rot: [ // Z
    [[0,0],[1,0],[1,1],[2,1]], [[2,0],[1,1],[2,1],[1,2]],
    [[0,1],[1,1],[1,2],[2,2]], [[1,0],[0,1],[1,1],[0,2]] ] },
  { color: '#64b5f6', rot: [ // J
    [[0,0],[0,1],[1,1],[2,1]], [[1,0],[2,0],[1,1],[1,2]],
    [[0,1],[1,1],[2,1],[2,2]], [[1,0],[1,1],[0,2],[1,2]] ] },
  { color: '#ffb74d', rot: [ // L
    [[2,0],[0,1],[1,1],[2,1]], [[1,0],[1,1],[1,2],[2,2]],
    [[0,1],[1,1],[2,1],[0,2]], [[0,0],[1,0],[1,1],[1,2]] ] },
];

type Cell = string | null;
type Board = Cell[][];
interface Piece { type: number; rotation: number; x: number; y: number; }

function emptyBoard(): Board {
  return Array.from({ length: ROWS }, () => Array<Cell>(COLS).fill(null));
}

function pieceCells(p: Piece): [number, number][] {
  return SHAPES[p.type].rot[p.rotation].map(([cx, cy]) => [p.x + cx, p.y + cy]);
}

function collides(board: Board, p: Piece): boolean {
  return pieceCells(p).some(([x, y]) => x < 0 || x >= COLS || y >= ROWS || (y >= 0 && board[y][x]));
}

function randType(): number {
  return Math.floor(Math.random() * SHAPES.length);
}

const GameTetris: React.FC<Props> = ({ onExit, challengeScore }) => {
  const prog = getProgress('tetris');
  const [level, setLvl] = useState(prog.level || 1);
  const [board, setBoard] = useState<Board>(emptyBoard);
  const [piece, setPiece] = useState<Piece>(() => ({ type: randType(), rotation: 0, x: 3, y: -1 }));
  const [nextType, setNextType] = useState(randType);
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [best] = useState(getHighScore('tetris'));
  const [over, setOver] = useState(false);
  const [cleared, setCleared] = useState(false);
  const [paused, setPaused] = useState(false);
  const savedRef = useRef(false);
  const stateRef = useRef({ board, piece, over, cleared, paused });
  stateRef.current = { board, piece, over, cleared, paused };

  const speed = START_SPEED[Math.min(level - 1, START_SPEED.length - 1)];
  // In-game speed-up: every 5 lines shaves the interval a touch.
  const effSpeed = Math.max(80, speed - Math.floor(lines / 5) * 40);

  const reset = useCallback((lvl: number) => {
    setBoard(emptyBoard());
    setPiece({ type: randType(), rotation: 0, x: 3, y: -1 });
    setNextType(randType());
    setScore(0);
    setLines(0);
    setOver(false);
    setCleared(false);
    setPaused(false);
    savedRef.current = false;
    setLvl(lvl);
    setLevel('tetris', lvl);
  }, []);

  const lockPiece = useCallback((b: Board, p: Piece) => {
    const nb = b.map(r => r.slice());
    pieceCells(p).forEach(([x, y]) => { if (y >= 0) nb[y][x] = SHAPES[p.type].color; });
    // Clear full rows.
    let clearedRows = 0;
    const kept = nb.filter(row => {
      const full = row.every(c => c);
      if (full) clearedRows++;
      return !full;
    });
    while (kept.length < ROWS) kept.unshift(Array<Cell>(COLS).fill(null));

    if (clearedRows > 0) {
      sfx.score();
      const pts = [0, 40, 100, 300, 1200][clearedRows] * level;
      setScore(s => s + pts);
      setLines(l => {
        const nl = l + clearedRows;
        if (nl >= LINE_GOAL) setCleared(true);
        return nl;
      });
    }
    // Spawn next.
    const np: Piece = { type: nextType, rotation: 0, x: 3, y: -1 };
    setNextType(randType());
    if (collides(kept, np)) {
      setBoard(kept);
      setOver(true);
      return;
    }
    setBoard(kept);
    setPiece(np);
  }, [level, nextType]);

  const step = useCallback(() => {
    const s = stateRef.current;
    if (s.over || s.cleared || s.paused) return;
    const moved = { ...s.piece, y: s.piece.y + 1 };
    if (collides(s.board, moved)) {
      lockPiece(s.board, s.piece);
    } else {
      setPiece(moved);
    }
  }, [lockPiece]);

  // Gravity loop.
  useEffect(() => {
    if (over || cleared) return;
    const id = setInterval(step, effSpeed);
    return () => clearInterval(id);
  }, [step, effSpeed, over, cleared]);

  const tryMove = useCallback((dx: number, dRot: number) => {
    const s = stateRef.current;
    if (s.over || s.cleared || s.paused) return;
    const np: Piece = {
      ...s.piece,
      x: s.piece.x + dx,
      rotation: (s.piece.rotation + dRot + 4) % 4,
    };
    // Simple wall-kick: try shifting if rotation collides.
    if (!collides(s.board, np)) { setPiece(np); return; }
    if (dRot !== 0) {
      for (const kick of [-1, 1, -2, 2]) {
        const kp = { ...np, x: np.x + kick };
        if (!collides(s.board, kp)) { setPiece(kp); return; }
      }
    }
  }, []);

  const softDrop = useCallback(() => {
    const s = stateRef.current;
    if (s.over || s.cleared || s.paused) return;
    const np = { ...s.piece, y: s.piece.y + 1 };
    if (collides(s.board, np)) lockPiece(s.board, s.piece);
    else { setPiece(np); setScore(sc => sc + 1); }
  }, [lockPiece]);

  const hardDrop = useCallback(() => {
    const s = stateRef.current;
    if (s.over || s.cleared || s.paused) return;
    let p = s.piece;
    let dist = 0;
    while (!collides(s.board, { ...p, y: p.y + 1 })) { p = { ...p, y: p.y + 1 }; dist++; }
    setScore(sc => sc + dist * 2);
    lockPiece(s.board, p);
  }, [lockPiece]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft': case 'a': e.preventDefault(); tryMove(-1, 0); break;
        case 'ArrowRight': case 'd': e.preventDefault(); tryMove(1, 0); break;
        case 'ArrowUp': case 'w': e.preventDefault(); tryMove(0, 1); break;
        case 'ArrowDown': case 's': e.preventDefault(); softDrop(); break;
        case ' ': e.preventDefault(); hardDrop(); break;
        case 'p': case 'P': setPaused(p => !p); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tryMove, softDrop, hardDrop]);

  // Persist on end.
  useEffect(() => {
    if ((over || cleared) && !savedRef.current) {
      savedRef.current = true;
      recordScore('tetris', score, level);
      if (cleared) { unlockNext('tetris', level); sfx.win(); } else sfx.lose();
    }
  }, [over, cleared, score, level]);

  // Render board with the active piece overlaid.
  const display = board.map(r => r.slice());
  if (!over) pieceCells(piece).forEach(([x, y]) => { if (y >= 0 && y < ROWS && x >= 0 && x < COLS) display[y][x] = SHAPES[piece.type].color; });

  return (
    <div className="gtetris">
      <div className="game-toolbar">
        <button className="game-back" onClick={onExit}>← Arcade</button>
        <div className="game-stats">
          <span className="gstat"><b>{score}</b><i>score</i></span>
          <span className="gstat"><b>{Math.max(best, score)}</b><i>best</i></span>
          <span className="gstat"><b>{lines}/{LINE_GOAL}</b><i>lines</i></span>
        </div>
        <button className="game-restart" onClick={() => reset(level)}>↻ New</button>
      </div>

      <div className="g2048-levels">
        {START_SPEED.map((_, i) => {
          const lvl = i + 1;
          const unlocked = lvl <= getProgress('tetris').unlockedLevel;
          return (
            <button key={lvl} className={`lvl-pill ${level === lvl ? 'active' : ''} ${unlocked ? '' : 'locked'}`}
              onClick={() => unlocked && reset(lvl)} disabled={!unlocked} title={unlocked ? `Start speed ${lvl}` : 'Locked'}>
              {unlocked ? `Lvl ${lvl}` : '🔒'}
            </button>
          );
        })}
      </div>

      {challengeScore != null && (
        <div className="challenge-target">🎯 Beat the challenge: <b>{challengeScore}</b> points</div>
      )}

      <div className="gtetris-stage">
        <div className="gtetris-board" style={{ width: COLS * CELL, height: ROWS * CELL }}>
          {display.map((row, y) =>
            row.map((c, x) => (
              <div key={`${x}-${y}`} className="gtetris-cell"
                style={{ left: x * CELL, top: y * CELL, width: CELL, height: CELL, background: c || undefined }} />
            ))
          )}
          {paused && <div className="gtetris-pause">Paused</div>}
        </div>
        <div className="gtetris-side">
          <div className="gtetris-next-label">Next</div>
          <div className="gtetris-next">
            {SHAPES[nextType].rot[0].map(([x, y], i) => (
              <div key={i} className="gtetris-cell" style={{ left: x * 16, top: y * 16, width: 16, height: 16, background: SHAPES[nextType].color }} />
            ))}
          </div>
          <button className="g-btn small" onClick={() => setPaused(p => !p)}>{paused ? '▶ Resume' : '⏸ Pause'}</button>
        </div>
      </div>

      {/* Touch controls */}
      <div className="gtetris-controls">
        <button onClick={() => tryMove(-1, 0)}>◀</button>
        <button onClick={() => tryMove(0, 1)}>⟳</button>
        <button onClick={() => tryMove(1, 0)}>▶</button>
        <button onClick={softDrop}>▼</button>
        <button onClick={hardDrop}>⤓ Drop</button>
      </div>

      {(over || cleared) && (
        <div className="game-overlay">
          <div className="game-overlay-card">
            <div className="go-emoji">{cleared ? '🏆' : '🧱'}</div>
            <h3>{cleared ? `Level ${level} cleared!` : 'Stack topped out'}</h3>
            <p>You scored <b>{score}</b> points · {lines} lines{cleared && level < START_SPEED.length ? ' · next level unlocked' : ''}</p>
            <div className="go-actions">
              {cleared && level < START_SPEED.length && (
                <button className="g-btn primary" onClick={() => reset(level + 1)}>Next level →</button>
              )}
              <button className="g-btn" onClick={() => reset(level)}>Play again</button>
              <button className="g-btn ghost" onClick={onExit}>Back to arcade</button>
            </div>
          </div>
        </div>
      )}

      <p className="game-hint">← → move · ↑ / ⟳ rotate · ↓ soft drop · space hard drop · P pause</p>
    </div>
  );
};

export default GameTetris;
